import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu.jsx';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Youtube,
  Plus,
  Search,
  Bookmark,
  Edit3,
  Trash2,
  Save,
  Heart,
  MoreVertical,
  ChevronRight,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferenciasPage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('references.title'));
  const { theme: themeColors, isDarkMode } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();
  const theme = isDarkMode ? 'dark' : 'light';
  const [canais, setCanais] = useState([]);
  const [filteredCanais, setFilteredCanais] = useState([]);
  const [selectedNicho, setSelectedNicho] = useState('all');
  const [selectedCanalFilter, setSelectedCanalFilter] = useState('all');
  const [canaisDoUsuario, setCanaisDoUsuario] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nichosDisponiveis, setNichosDisponiveis] = useState([]);
  const [expandedNichos, setExpandedNichos] = useState({});
  const [canaisPorNicho, setCanaisPorNicho] = useState({});

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCanal, setEditingCanal] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    url: '',
    nicho: '',
    notas: '',
    canal_id: ''
  });

  useEffect(() => {
    fetchCanais();
    fetchCanaisDoUsuario();
    checkIfAdmin();
  }, []);

  useEffect(() => {
    filterCanais();
    extractNichos();
  }, [selectedNicho, selectedCanalFilter, searchQuery, canais]);

  const fetchCanaisDoUsuario = async () => {
    try {
      const response = await axios.get(`${API}/canais`);
      let allCanais = response.data;
      // Cache for sidebar dynamic channel list
      try {
        sessionStorage.setItem('sidebar-channels', JSON.stringify(response.data));
        window.dispatchEvent(new Event('sidebar-channels-updated'));
      } catch {};
      // Include shared channels
      try {
        const sharedResponse = await axios.get(`${API}/canais/compartilhados`);
        const sharedAceitos = (sharedResponse.data || []).filter(c => c.comp_status === 'aceito');
        if (sharedAceitos.length > 0) {
          allCanais = [...allCanais, ...sharedAceitos.map(c => ({ ...c, _is_shared: true }))];
        }
      } catch {}
      setCanaisDoUsuario(allCanais);
    } catch (error) {
      console.error('Erro ao carregar canais do usuário:', error);
    }
  };

  const extractNichos = () => {
    const nichos = [...new Set(canais.map(c => c.nicho).filter(n => n && n.trim() !== ''))];
    setNichosDisponiveis(nichos.sort());
  };

  const checkIfAdmin = () => {
    axios.get(`${API}/auth/me`)
      .then((response) => {
        setIsAdmin(response.data.is_super_admin || false);
      })
      .catch(() => setIsAdmin(false));
  };

  const fetchCanais = async () => {
    try {
      const response = await axios.get(`${API}/referencias`);
      const sorted = response.data.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
      setCanais(sorted);
      setLoading(false);
    } catch (error) {
      toast.error(t('references.error_load'));
      setLoading(false);
    }
  };

  const filterCanais = () => {
    let filtered = canais;

    if (selectedCanalFilter !== 'all') {
      if (selectedCanalFilter === 'global') {
        filtered = filtered.filter(c => !c.canal_id);
      } else {
        filtered = filtered.filter(c => c.canal_id === selectedCanalFilter);
      }
    }

    if (selectedNicho !== 'all') {
      filtered = filtered.filter(c => c.nicho === selectedNicho);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.nome_canal?.toLowerCase().includes(query) ||
        (c.anotacoes && c.anotacoes.toLowerCase().includes(query)) ||
        (c.nicho && c.nicho.toLowerCase().includes(query))
      );
    }

    setFilteredCanais(filtered);

    const grouped = {};
    filtered.forEach(canal => {
      const nicho = canal.nicho || 'Sem Nicho';
      if (!grouped[nicho]) {
        grouped[nicho] = [];
      }
      grouped[nicho].push(canal);
    });

    setCanaisPorNicho(grouped);
  };

  const resetForm = () => {
    setFormData({
      url: '',
      nicho: '',
      notas: '',
      canal_id: ''
    });
  };

  const normalizarNicho = (nicho) => {
    if (!nicho) return '';

    return nicho
      .trim()
      .replace(/\s+/g, ' ')
      .split(' ')
      .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase())
      .join(' ');
  };

  const handleCreateCanal = async (e) => {
    e.preventDefault();

    if (!formData.url) {
      toast.error(t('references.url_required'));
      return;
    }

    setSaving(true);
    try {
      const nichoNormalizado = normalizarNicho(formData.nicho);

      const dataToSend = {
        nome_canal: 'Canal',
        url_canal: formData.url,
        nicho: nichoNormalizado,
        anotacoes: formData.notas || '',
        canal_id: formData.canal_id || null
      };
      const response = await axios.post(`${API}/referencias`, dataToSend);
      await fetchCanais();
      setShowCreateDialog(false);
      resetForm();
      toast.success(t('references.channel_saved'));
    } catch (error) {
      toast.error(t('references.error_save'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditCanal = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      const nichoNormalizado = normalizarNicho(formData.nicho);

      const updateData = {
        nome_canal: editingCanal.nome_canal,
        url_canal: formData.url,
        foto_url: editingCanal.foto_url,
        nicho: nichoNormalizado,
        anotacoes: formData.notas || '',
        canal_id: formData.canal_id || null
      };

      await axios.put(`${API}/referencias/${editingCanal.id}`, updateData);
      await fetchCanais();
      setShowEditDialog(false);
      setEditingCanal(null);
      resetForm();
      toast.success(t('references.channel_updated'));
    } catch (error) {
      toast.error(t('references.error_update'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCanal = async (canalId) => {
    if (!window.confirm(t('references.confirm_delete'))) return;

    try {
      await axios.delete(`${API}/referencias/${canalId}`);
      setCanais(canais.filter(c => c.id !== canalId));
      toast.success(t('references.channel_deleted'));
    } catch (error) {
      toast.error(t('references.error_delete'));
    }
  };

  const openEditDialog = (canal) => {
    setEditingCanal(canal);
    setFormData({
      url: canal.url_canal || '',
      nicho: canal.nicho || '',
      notas: canal.anotacoes || '',
      canal_id: canal.canal_id || ''
    });
    setShowEditDialog(true);
  };

  const toggleNicho = (nicho) => {
    setExpandedNichos(prev => ({
      ...prev,
      [nicho]: !prev[nicho]
    }));
  };

  const getCanalNome = (canalId) => {
    if (!canalId) return t('references.scope_global');
    const canal = canaisDoUsuario.find(c => c.id === canalId);
    return canal ? canal.nome : 'Canal';
  };

  // --- Shared styles ---
  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--dp-bg-input, #252525)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: '8px',
    color: 'var(--dp-text-primary)',
    fontFamily: 'var(--dp-font)',
    fontSize: '13px',
    outline: 'none',
    transition: 'all 0.3s',
    boxSizing: 'border-box',
  };

  const textareaStyle = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: '80px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--dp-text-secondary)',
    fontFamily: 'var(--dp-font)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const hintStyle = {
    fontSize: '11px',
    color: 'var(--dp-text-tertiary)',
    fontFamily: 'var(--dp-font)',
    marginTop: '4px',
    lineHeight: 1.4,
  };

  const btnPrimaryStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '9px 18px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
    border: 'none',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  };

  const btnSecondaryStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    padding: '9px 20px',
    borderRadius: '8px',
    background: 'transparent',
    border: '1px solid var(--dp-border-subtle)',
    color: 'var(--dp-text-secondary)',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    transition: 'all 0.25s',
    flex: 1,
  };

  const btnSubmitStyle = {
    ...btnPrimaryStyle,
    justifyContent: 'center',
    flex: 1,
    opacity: saving ? 0.7 : 1,
    pointerEvents: saving ? 'none' : 'auto',
  };

  const dialogOverlayStyle = {
    background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid var(--dp-border-medium)',
    borderRadius: '16px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
    maxWidth: '480px',
    maxHeight: '85vh',
    overflow: 'auto',
    padding: '24px',
  };

  const cardMenuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background 0.15s',
    border: 'none',
    width: '100%',
    background: 'transparent',
  };

  // --- Dropdown options ---
  const canalFilterOptions = [
    { value: 'all', label: t('references.all_references') },
    { value: 'global', label: t('references.global') },
    ...canaisDoUsuario.map(c => ({ value: c.id, label: c.nome }))
  ];

  const nichoFilterOptions = [
    { value: 'all', label: t('references.all_niches') },
    ...nichosDisponiveis.map(n => ({ value: n, label: n }))
  ];

  const linkOptions = [
    { value: 'global', label: t('references.global') },
    ...canaisDoUsuario.map(c => ({ value: c.id, label: c.nome }))
  ];

  // --- Form renderer (shared between create and edit) ---
  const renderForm = (onSubmit, isEdit) => (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {/* URL */}
      <div>
        <label style={labelStyle}>{t('references.youtube_url_required')}</label>
        <input
          type="text"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          required
          placeholder={t('references.youtube_url_placeholder')}
          style={{ ...inputStyle, fontFamily: 'monospace, var(--dp-font)' }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
        />
        <div style={hintStyle}>
          {isEdit ? t('references.youtube_url_edit_hint') : t('references.youtube_url_example')}
        </div>
      </div>

      {/* Link to channel */}
      <div>
        <label style={labelStyle}>{t('references.link_channel')}</label>
        <CustomDropdown
          options={linkOptions}
          value={formData.canal_id || 'global'}
          onChange={(val) => setFormData({ ...formData, canal_id: val === 'global' ? '' : val })}
          placeholder={t('references.global')}
        />
        <div style={hintStyle}>
          {isEdit ? t('references.link_edit_hint') : t('references.link_global_hint')}
        </div>
      </div>

      {/* Niche */}
      <div>
        <label style={labelStyle}>{t('references.niche_required')}</label>
        <input
          type="text"
          value={formData.nicho}
          onChange={(e) => setFormData({ ...formData, nicho: e.target.value })}
          required
          placeholder={t('references.niche_placeholder')}
          list={isEdit ? 'nichos-sugestoes-edit' : 'nichos-sugestoes'}
          style={inputStyle}
          onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
        />
        {nichosDisponiveis.length > 0 && (
          <datalist id={isEdit ? 'nichos-sugestoes-edit' : 'nichos-sugestoes'}>
            {nichosDisponiveis.map(nicho => (
              <option key={nicho} value={nicho} />
            ))}
          </datalist>
        )}
        <div style={hintStyle}>
          {nichosDisponiveis.length > 0
            ? t('references.niche_suggestions_hint')
            : t('references.niche_first_hint')}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label style={labelStyle}>{t('references.notes')}</label>
        <textarea
          value={formData.notas}
          onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
          rows={3}
          placeholder={t('references.notes_placeholder')}
          style={textareaStyle}
          onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
        />
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '10px', paddingTop: '6px' }}>
        <button
          type="button"
          onClick={() => isEdit ? setShowEditDialog(false) : setShowCreateDialog(false)}
          disabled={saving}
          style={btnSecondaryStyle}
        >
          {t('references.cancel')}
        </button>
        <button type="submit" disabled={saving} className="dp-btn-cta dp-btn-cta--sm" style={{ flex: 1 }}
        >
          <Save size={14} />
          {saving ? t('references.saving') : (isEdit ? t('references.save_changes') : t('references.save_channel'))}
          <ArrowRight size={14} />
        </button>
      </div>
    </form>
  );

  // --- Loading state ---
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-bg-primary)',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--dp-border-subtle)',
          borderTop: '3px solid var(--dp-blue-500, #0DAEFF)',
          borderRadius: '50%',
          animation: 'dpRefSpin 1s linear infinite',
        }} />
        <style>{`
          @keyframes dpRefSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Page Header */}
        <PageHeader
          icon={<Bookmark size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('references.title')}
          description={t('references.subtitle')}
        >
          <button
            className="dp-btn-cta dp-btn-cta--sm"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus size={14} /> {t('references.new_channel')} <ArrowRight size={14} />
          </button>
        </PageHeader>

        {/* Toolbar: Filters + Search */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '24px',
          alignItems: 'flex-end',
        }}>
          {/* Channel filter */}
          <div style={{ width: '220px', minWidth: '180px' }}>
            <CustomDropdown
              options={canalFilterOptions}
              value={selectedCanalFilter}
              onChange={setSelectedCanalFilter}
              placeholder={t('references.filter_by_channel')}
            />
          </div>

          {/* Niche filter */}
          <div style={{ width: '220px', minWidth: '180px' }}>
            <CustomDropdown
              options={nichoFilterOptions}
              value={selectedNicho}
              onChange={setSelectedNicho}
              placeholder={t('references.filter_by_niche')}
            />
          </div>

          {/* Search */}
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--dp-text-tertiary)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder={t('references.search_channels')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                ...inputStyle,
                paddingLeft: '36px',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
          <DialogContent
            style={dialogOverlayStyle}
            onPointerDownOutside={(e) => {
              // Don't close dialog when clicking on CustomDropdown portal
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]')) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
                margin: 0,
              }}>
                {t('references.add_reference')}
              </DialogTitle>
              <p style={{
                fontSize: '13px',
                color: 'var(--dp-text-tertiary)',
                fontFamily: 'var(--dp-font)',
                margin: '6px 0 0 0',
                lineHeight: 1.4,
              }}>
                {t('references.add_reference_desc')}
              </p>
            </DialogHeader>
            <div style={{ marginTop: '20px' }}>
              {renderForm(handleCreateCanal, false)}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { resetForm(); setEditingCanal(null); } }}>
          <DialogContent
            style={dialogOverlayStyle}
            onPointerDownOutside={(e) => {
              // Don't close dialog when clicking on CustomDropdown portal
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]')) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              const target = e.target;
              if (target?.closest?.('[role="listbox"]') || target?.closest?.('[role="option"]') || target?.closest?.('[data-custom-dropdown]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
                margin: 0,
              }}>
                {t('references.edit_reference')}
              </DialogTitle>
            </DialogHeader>
            <div style={{ marginTop: '20px' }}>
              {renderForm(handleEditCanal, true)}
            </div>
          </DialogContent>
        </Dialog>

        {/* Content: Nicho sections or empty state */}
        {filteredCanais.length === 0 ? (
          /* Empty State */
          <div style={{
            borderRadius: '16px',
            padding: '60px 32px',
            textAlign: 'center',
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: 'rgba(167,139,250,0.10)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px auto',
            }}>
              <Heart size={32} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
              margin: '0 0 8px 0',
            }}>
              {canais.length === 0 ? t('references.no_channels') : t('references.no_channels_found')}
            </h3>
            <p style={{
              fontSize: '13px',
              color: 'var(--dp-text-tertiary)',
              fontFamily: 'var(--dp-font)',
              margin: '0 0 24px 0',
            }}>
              {canais.length === 0 ? t('references.start_adding') : t('references.adjust_filters')}
            </p>
            {canais.length === 0 && (
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus size={14} /> {t('references.add_first')} <ArrowRight size={14} />
              </button>
            )}
          </div>
        ) : (
          /* Niche sections */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.keys(canaisPorNicho).sort().map((nicho, nichoIndex) => {
              const canaisDoNicho = canaisPorNicho[nicho];
              const isExpanded = expandedNichos[nicho];

              return (
                <div
                  key={nicho}
                  className="dp-top-accent"
                  style={{
                    background: 'var(--dp-bg-card)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    animation: `dpRefFadeInUp 0.4s ease ${nichoIndex * 0.06}s both`,
                  }}
                >
                  {/* Nicho Header - Clickable */}
                  <div
                    onClick={() => toggleNicho(nicho)}
                    style={{
                      padding: '16px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: isExpanded
                        ? 'rgba(13, 174, 255, 0.04)'
                        : 'transparent',
                      borderBottom: isExpanded ? '1px solid var(--dp-border-subtle)' : '1px solid transparent',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.background = 'rgba(13, 174, 255, 0.03)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <ChevronRight
                        size={20}
                        style={{
                          color: 'var(--dp-blue-500, #0DAEFF)',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: 'var(--dp-text-primary)',
                          fontFamily: 'var(--dp-font)',
                          lineHeight: 1.3,
                        }}>
                          {nicho}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--dp-text-tertiary)',
                          fontFamily: 'var(--dp-font)',
                          marginTop: '2px',
                        }}>
                          {canaisDoNicho.length === 1
                            ? t('references.channel_count', { count: canaisDoNicho.length })
                            : t('references.channels_count', { count: canaisDoNicho.length })}
                        </div>
                      </div>
                    </div>

                    {/* Badge pill */}
                    <div style={{
                      minWidth: '28px',
                      height: '28px',
                      borderRadius: '14px',
                      background: 'rgba(13, 174, 255, 0.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 10px',
                      flexShrink: 0,
                    }}>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'var(--dp-blue-500, #0DAEFF)',
                        fontFamily: 'var(--dp-font)',
                      }}>
                        {canaisDoNicho.length}
                      </span>
                    </div>
                  </div>

                  {/* Cards Grid - Expandable */}
                  {isExpanded && (
                    <div style={{
                      padding: '16px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                      gap: '12px',
                      animation: 'dpRefSlideDown 0.3s ease-out',
                      overflow: 'hidden',
                    }}>
                      {canaisDoNicho.map((canal, cardIndex) => (
                        <div
                          key={canal.id}
                          style={{
                            background: 'var(--dp-bg-card)',
                            border: '1px solid var(--dp-border-subtle)',
                            borderRadius: '12px',
                            padding: '18px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            position: 'relative',
                            animation: `dpRefFadeInUp 0.35s ease ${cardIndex * 0.04}s both`,
                          }}
                          onClick={() => window.open(canal.url_canal, '_blank')}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = isDarkMode
                              ? '0 8px 24px rgba(13, 174, 255, 0.12)'
                              : '0 8px 24px rgba(0, 0, 0, 0.08)';
                            e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                          }}
                        >
                          {/* Top row: Avatar + Name + Menu */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '10px',
                          }}>
                            {/* Avatar */}
                            <div style={{ flexShrink: 0 }}>
                              {canal.foto_url ? (
                                <img
                                  src={canal.foto_url}
                                  alt={canal.nome_canal}
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid var(--dp-border-subtle)',
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M17 18a5 5 0 0 0-10 0"/%3E%3Ccircle cx="12" cy="7" r="4"/%3E%3C/svg%3E';
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #7C3AED)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 700,
                                  color: 'white',
                                  fontFamily: 'var(--dp-font)',
                                }}>
                                  {canal.nome_canal ? canal.nome_canal.charAt(0).toUpperCase() : <Youtube size={20} color="white" />}
                                </div>
                              )}
                            </div>

                            {/* Channel Name */}
                            <div
                              title={canal.nome_canal}
                              style={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: 'var(--dp-text-primary)',
                                fontFamily: 'var(--dp-font)',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: 1.3,
                              }}
                            >
                              {canal.nome_canal}
                            </div>

                            {/* Menu Dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--dp-text-tertiary)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
                                    e.currentTarget.style.color = 'var(--dp-text-primary)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                                  }}
                                >
                                  <MoreVertical size={16} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                style={{
                                  background: 'var(--dp-bg-elevated, rgba(30,30,36,0.95))',
                                  backdropFilter: 'blur(16px)',
                                  border: '1px solid var(--dp-border-medium)',
                                  borderRadius: '10px',
                                  padding: '4px',
                                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                                  minWidth: '140px',
                                }}
                              >
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditDialog(canal);
                                  }}
                                  style={{
                                    ...cardMenuItemStyle,
                                    color: 'var(--dp-text-secondary)',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.08)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                                >
                                  <Edit3 size={14} />
                                  {t('references.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCanal(canal.id);
                                  }}
                                  style={{
                                    ...cardMenuItemStyle,
                                    color: '#EF4444',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <Trash2 size={14} />
                                  {t('references.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Scope Badge */}
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '3px 10px',
                            borderRadius: '9999px',
                            fontSize: '11px',
                            fontWeight: 600,
                            fontFamily: 'var(--dp-font)',
                            marginBottom: canal.anotacoes ? '8px' : '0',
                            background: !canal.canal_id
                              ? 'rgba(167,139,250,0.12)'
                              : 'rgba(13,174,255,0.10)',
                            color: !canal.canal_id
                              ? '#A78BFA'
                              : 'var(--dp-blue-500, #0DAEFF)',
                            letterSpacing: '0.3px',
                          }}>
                            {!canal.canal_id
                              ? t('references.scope_global')
                              : t('references.linked_to', { name: getCanalNome(canal.canal_id) })}
                          </div>

                          {/* Notes */}
                          {canal.anotacoes && (
                            <div style={{
                              fontSize: '12px',
                              color: 'var(--dp-text-tertiary)',
                              fontFamily: 'var(--dp-font)',
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              marginTop: '4px',
                            }}>
                              {canal.anotacoes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Animations */}
      <style>{`
        @keyframes dpRefFadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dpRefSlideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 2000px;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ReferenciasPage;
